import React from "react";
import PropTypes from "prop-types";
import Drawer from "@mui/material/Drawer";
import { SFStack } from "../grid";
import SFTypography from "../typography";
import { SFIconButton } from "../buttons";
import { SFCloseIcon } from "../icons";
import useStyled from "../../hooks/useStyled";

const { styled } = useStyled();

const StyledHeader = styled(SFStack)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.grey550.main,
  color: theme.palette.white.main,
  zIndex: 2,
  padding: theme.spacing(1.5),
  paddingLeft: theme.spacing(2.5),
  boxShadow: "0px 7px 12px 0px rgba(0, 0, 0, 0.15)",
}));

const StyledBody = styled("div", { shouldForwardProp: false })(
  ({ theme, disableGutters, disablePadding }) => ({
    backgroundColor: theme.palette.grey550.main,
    color: theme.palette.grey100.main,
    display: "flex",
    flexDirection: "column",
    // These two properties allow the body to fill the available space
    // without affecting the footer
    height: 0,
    flex: "1 1 auto",
    overflowY: "scroll",
    // paddingBottom: theme.spacing(2.5),
    ...(!disableGutters && {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    }),
    ...(!disablePadding && {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    }),
  })
);

const StyledFooterWrapper = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.grey550.main,
  position: "sticky",
  bottom: 0,
  padding: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  boxShadow: "0px -7px 12px 0px rgba(0, 0, 0, 0.2)",
}));

const StyledWrapper = styled(SFStack, {
  shouldForwardProp: false,
})(({ theme, width }) => ({
  width: width,
  flex: 1,
}));

const StyledIconButton = styled(SFIconButton)(({ theme }) => ({
  borderRadius: "50%",
  "&:hover": {
    background: theme.palette.grey600.main,
  },
}));

const StyledTypography = styled(SFTypography)(({ theme }) => ({
  textTransform: "uppercase",
}));

const SFDrawer = ({
  open,
  side = "right",
  header,
  footer,
  onClose,
  children,
  size = "s",
  disableGutters = false,
  disablePadding = false,
  disableHeaderBottomMargin = false,
  headerOptions,
  ignoreHtml2Canvas = false,
}) => {
  let width = 400;

  switch (size) {
  case "xs":
    width = 300;
    break;
  case "s":
    width = 400;
    break;
  case "m":
    width = 500;
    break;
  case "l":
    width = 600;
    break;
  case "xl":
    width = 700;
    break;
  case "xxl":
    width = 800;
    break;
  default:
    width = 400;
  }

  const toggleDrawer = (open) => (event) => {
    onClose();
  };
  return (
    <Drawer
      {...(ignoreHtml2Canvas && { "data-html2canvas-ignore": true })}
      anchor={side}
      open={open}
      onClose={toggleDrawer(false)}
      sx={{
        //zIndex: 9999,
        "& > .MuiPaper-root": {
          overflow: "-moz-scrollbars-vertical",
          overflowY: "scroll",
        },
      }}
    >
      <StyledWrapper width={width}>
        <StyledHeader>
          <SFStack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <StyledTypography variant="body2">{header}</StyledTypography>
            <SFStack direction="row" alignItems="center">
              {headerOptions}
              <StyledIconButton
                variant="transparent"
                onClick={toggleDrawer(false)}
                size="small"
              >
                <SFCloseIcon />
              </StyledIconButton>
            </SFStack>
          </SFStack>
        </StyledHeader>
        <StyledBody
          disableGutters={disableGutters}
          disablePadding={disablePadding}
        >
          {children}
        </StyledBody>
      </StyledWrapper>
      {footer && (
        <StyledFooterWrapper>
          <div className="footer">
            {footer}
          </div>
        </StyledFooterWrapper>
      )}
    </Drawer>
  );
};

SFDrawer.propTypes = {
  open: PropTypes.bool,
  side: PropTypes.oneOf(["top", "left", "bottom", "right"]),
  size: PropTypes.oneOf(["xs", "s", "m", "l", "xl", "xxl"]),
  onClose: PropTypes.func,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  footer: PropTypes.node,
  headerOptions: PropTypes.object,
};

export default SFDrawer;
